import { type GridColDef } from '@mui/x-data-grid'
import { DataTable } from 'components/Shared/DataTable'
import { formatDateTime } from 'utils/util'
import NoPatronsCheckedInIcon from 'assets/NoPatronsCheckedIn.png'
import { colorPrimitives } from 'components/Theme'
import { formatPhoneNumber } from '@jjvgaming/player-payback-library'
import { Box, Button, Stack, Typography } from '@mui/material'
import { ReportExportModal } from '../PlayerPaybackReportExportModal'
import { useState } from 'react'
import { useSnackbar } from 'stores/useSnackbar'
import ExportIcon from 'assets/export.svg'
import { useGetRewardRedemptionsByLeId } from 'hooks/api/Reports/useGetRewardRedemptionsByLeId'
import { useSetLe } from 'stores/useSetLe'
import { ActivityIndicator } from 'components/Shared/ActivityIndicator'
import { ErrorIndicator } from 'components/Shared/ErrorIndicator'

interface PatronCheckinsTableProps {
  piiPermission: boolean
  downloadPIIPermission: boolean
  startDate: Date
  endDate: Date
}

const columns: GridColDef[] = [
  {
    field: 'transactionDate',
    headerName: 'Transaction Date',
    minWidth: 225,
    flex: 0.75,
    valueGetter: (params) => params.row.transactionDate,
    valueFormatter: ({ value }) => formatDateTime(value),
  },
  {
    field: 'patronId',
    headerName: 'Patron ID',
    minWidth: 100,
    flex: 0.5,
    valueFormatter: ({ value }) => `#${value}`,
  },
  {
    field: 'rewardsCatalogReward.reward',
    headerName: 'Redeemed Reward',
    minWidth: 200,
    flex: 0.75,
    valueGetter: (params) => params.row.rewardsCatalogReward?.reward?.name,
  },
  {
    field: 'id',
    headerName: 'Reference #',
    minWidth: 200,
    flex: 0.75,
    valueGetter: (params) => params.row?.id,
  },
  {
    field: 'rewardsProgram.name',
    headerName: 'Reward Program Name',
    minWidth: 300,
    flex: 1,
    valueGetter: (params) => params.row.rewardsProgram.name,
  },
  {
    field: 'licensedEstablishment.standardName',
    headerName: 'Location',
    minWidth: 300,
    flex: 1,
    valueGetter: (params) =>
      params.row.licensedEstablishment
        ? params.row.licensedEstablishment.standardName
        : '',
  },
]

// PII permission columns
const columnsPII: GridColDef[] = [
  {
    field: 'transactionDate',
    headerName: 'Transaction Date',
    minWidth: 225,
    flex: 2,
    valueGetter: (params) => params.row.transactionDate,
    valueFormatter: ({ value }) => formatDateTime(value),
  },
  {
    field: 'patronId',
    headerName: 'Patron ID',
    minWidth: 100,
    flex: 1,
    valueFormatter: ({ value }) => `#${value}`,
  },
  {
    field: 'rewardsCatalogReward.reward',
    headerName: 'Redeemed Reward',
    minWidth: 225,
    flex: 1,
    valueGetter: (params) => params.row.rewardsCatalogReward?.reward?.name,
  },
  {
    field: 'id',
    headerName: 'Reference #',
    minWidth: 200,
    flex: 0.75,
    valueGetter: (params) => params.row?.id,
  },
  {
    field: 'rewardsProgram.name',
    headerName: 'Reward Program Name',
    minWidth: 300,
    flex: 1,
    valueGetter: (params) => params.row.rewardsProgram.name,
  },
  {
    field: 'licensedEstablishment.standardName',
    headerName: 'Location',
    minWidth: 300,
    flex: 1,
    valueGetter: (params) =>
      params.row.licensedEstablishment
        ? params.row.licensedEstablishment.standardName
        : '',
  },
  {
    field: 'firstName',
    headerName: 'First Name',
    minWidth: 120,
    flex: 1,
    valueGetter: (params) => params.row.patron.firstName,
  },
  {
    field: 'lastName',
    headerName: 'Last Name',
    minWidth: 120,
    flex: 1,
    valueGetter: (params) => params.row.patron.lastName,
  },
  {
    field: 'phoneNumber',
    headerName: 'Phone',
    minWidth: 140,
    flex: 1,
    valueGetter: (params) => params.row.patron.phoneNumber ?? '',
    valueFormatter: ({ value }) => formatPhoneNumber(value),
  },
  {
    field: 'email',
    headerName: 'Email',
    minWidth: 300,
    flex: 1,
    valueGetter: (params) => params.row.patron.email ?? '',
  },
  {
    field: 'addresses',
    minWidth: 400,
    headerName: 'Address',
    flex: 2,
    renderCell: (params) => {
      return (
        <p className="overflow-hidden whitespace-nowrap text-ellipsis">
          {(params.row.address &&
            `${params.row.address.address1}` +
              (params.row.address.address2
                ? `, ${params.row.address.address2}`
                : '')) ??
            null}
        </p>
      )
    },
    valueGetter: (params) => {
      return (
        (params.row.address &&
          `${params.row.address.address1}` +
            (params.row.address.address2
              ? `, ${params.row.address.address2}`
              : '')) ??
        null
      )
    },
  },
  {
    field: 'city',
    headerName: 'City',
    minWidth: 120,
    flex: 1,
    valueGetter: (params) =>
      params.row.address ? params.row.address.city : '',
  },
  {
    field: 'state',
    headerName: 'State',
    minWidth: 120,
    flex: 1,
    valueGetter: (params) =>
      params.row.address ? params.row.address.state : '',
  },
  {
    field: 'postalCode',
    headerName: 'Zip',
    minWidth: 120,
    flex: 1,
    valueGetter: (params) =>
      params.row.address ? params.row.address.postalCode : '',
  },
]

export const RewardRedemptionsTable = ({
  piiPermission,
  downloadPIIPermission,
  startDate,
  endDate,
}: PatronCheckinsTableProps) => {
  const { passedLe } = useSetLe()
  const setMessage = useSnackbar((state) => state.setMessage)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const toggleModal = () => setIsModalOpen(!isModalOpen)

  const reportQuery = useGetRewardRedemptionsByLeId({
    currentPage: 1,
    pageSize: 100,
    licensedEstablishmentId: Number(passedLe?.id),
    startDate,
    endDate,
  })

  return (
    <Stack spacing={2}>
      {reportQuery.isPending && reportQuery.isFetching && <ActivityIndicator />}
      {reportQuery.isError && <ErrorIndicator />}
      {reportQuery.data && (
        <>
          {downloadPIIPermission && (
            <Box className="flex lg:justify-end">
              <Button variant="contained" onClick={toggleModal}>
                <img
                  src={ExportIcon}
                  width={15}
                  height={15}
                  alt="Export icon"
                />
                <Typography variant="label-CTA">Export</Typography>
              </Button>
            </Box>
          )}
          <ReportExportModal
            isModalOpen={isModalOpen}
            toggleModal={toggleModal}
            reportName="Patron Reward Redemptions"
            exportColumns={piiPermission ? columnsPII : columns}
            setMessage={setMessage}
            licensedEstablishmentId={Number(passedLe?.id)}
            corporateAccountId={null}
            startDate={startDate}
            endDate={endDate}
            exportType="rewardRedemptions"
          />
          <DataTable
            slotProps={{ row: { style: { cursor: 'default' } } }}
            imageSource={NoPatronsCheckedInIcon}
            noDataMessage="There are no patron reward redemptions available"
            columns={piiPermission ? columnsPII : columns}
            rows={reportQuery.data?.results ?? []}
            sortFieldName="transactionDate"
            sort="desc"
            sx={{
              color: colorPrimitives.black,
              '& .MuiDataGrid-row:hover': { cursor: 'auto' },
            }}
          />
        </>
      )}
    </Stack>
  )
}
